
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import StreamingMessage from "./streaming-message";

export type MessageRole = "user" | "assistant" | "system";

export interface Message {
  id: string;
  content: string;
  role: MessageRole;
  timestamp: Date;
  isStreaming?: boolean;
  isWelcomeMessage?: boolean;
}

interface MessageItemProps {
  message: Message;
}

const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  const isUser = message.role === "user";
  const [streamingComplete, setStreamingComplete] = useState(false);
  
  const formatMessage = (content: string) => {
    // Format bold text (markdown style)
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Format bullet points with simple dashes
    content = content.replace(/^- (.+)$/gm, '<div class="mb-1">- $1</div>');
    
    // Clean and format Markdown links to be blue
    content = content.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
    );

    // Make any remaining raw URLs clickable with blue styling
    content = content.replace(
      /(?<!\(|"|=|'|<a href=")(https?:\/\/[^\s<]+)(?!"|>)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
    );

    // Format section headers without emojis
    content = content.replace(
      /^\*\*([^*]+)\*\*$/gm,
      '<div class="font-bold text-gray-800 mb-2">$1</div>'
    );

    // Format numbered lists
    content = content.replace(
      /^(\d+\.)\s*\*\*([^*]+)\*\*/gm,
      '<div class="mb-2"><strong>$1 $2</strong></div>'
    );

    // Format source information
    content = content.replace(
      /^\*\*Kilde:\*\*\s*(.+)$/gm,
      '<div class="mt-3 text-sm text-gray-600"><strong>Kilde:</strong> $1</div>'
    );

    // Note: Citations are already formatted as HTML from the citation-formatter
    // so we don't need additional processing here - they'll be rendered directly

    return content;
  };

  const handleStreamingComplete = () => {
    setStreamingComplete(true);
  };
  
  return (
    <div className={cn(
      "flex w-full py-4 px-8",
      isUser ? "bg-white" : "bg-white"
    )}>
      <div className="w-full max-w-5xl mx-auto">
        <div className={cn(
          "w-full",
          isUser ? "flex justify-end" : ""
        )}>
          <div className={cn(
            "whitespace-pre-wrap",
            isUser 
              ? "bg-soft-blue rounded-lg py-3 px-4 text-black inline-block max-w-[85%]"
              : "w-full leading-relaxed text-base"
          )}>
            {isUser ? (
              <div dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }} />
            ) : (
              <StreamingMessage 
                content={formatMessage(message.content)} 
                isComplete={!message.isStreaming || streamingComplete}
                onComplete={handleStreamingComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageItem;
